import classNames from 'classnames'
import {
  ChangeEvent,
  forwardRef,
  useState,
  HTMLInputTypeAttribute,
  ForwardedRef,
} from 'react'
import Icon, { IconProps } from './Icon'

type TextFieldProps = {
  className?: string
  containerClassName?: string
  disabled?: boolean
  endIcon?: IconProps
  error?: string
  helper?: string
  id?: string
  inputType?: HTMLInputTypeAttribute
  label?: string
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  leadingIcon?: IconProps
  limit?: number
  placeholder?: string
  value: string
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void
  onFocus?: (event: ChangeEvent<HTMLInputElement>) => void
} & React.InputHTMLAttributes<HTMLInputElement>

function limitValueLength(value: string, limit: number): string {
  if (value && value.length <= limit) {
    return value
  }
  return value.slice(0, limit)
}

function inputPaddingHorizontal(leadingIcon?: IconProps, endIcon?: IconProps) {
  let pl = 'pl-m'
  let pr = 'pr-m'

  if (leadingIcon) pl = ''
  if (endIcon) pr = ''

  return pl + ' ' + pr
}

const TextField = (
  {
    className,
    containerClassName,
    disabled,
    endIcon,
    error,
    helper,
    id,
    inputType,
    label,
    labelProps,
    leadingIcon,
    limit = Infinity,
    placeholder,
    value,
    onBlur,
    onFocus,
    ...nativeInputProps
  }: TextFieldProps,
  ref?: ForwardedRef<HTMLInputElement>,
) => {
  const [inputFocused, setInputFocused] = useState(false)
  return (
    <div className={classNames('babu-textfield-container', containerClassName)}>
      {label ? (
        <label
          htmlFor={id}
          {...labelProps}
          className={classNames(
            'font-semibold text-base text-text600',
            labelProps?.className,
          )}>
          {label}
        </label>
      ) : null}
      <div
        className={classNames(
          'flex justify-between border rounded-2xl items-center mt-xxs pr-s',
          error
            ? 'border-alert-danger'
            : inputFocused
            ? 'border-text600'
            : disabled
            ? 'border-bg200'
            : 'border-bg300',
        )}>
        {leadingIcon ? <Icon {...leadingIcon} /> : null}
        <input
          {...nativeInputProps}
          className={classNames(
            'my-s w-full',
            'text-lg placeholder:text-base-300 caret-primary900',
            'focus:border-none outline-0 focus:outline-none',
            inputPaddingHorizontal(leadingIcon, endIcon),
            className,
          )}
          id={id}
          onFocus={e => {
            setInputFocused(true)
            onFocus?.(e)
          }}
          onBlur={e => {
            setInputFocused(false)
            onBlur?.(e)
          }}
          placeholder={placeholder}
          type={inputType}
          value={limitValueLength(value, limit)}
          ref={ref}
        />
        {endIcon && value.length > 0 ? <Icon {...endIcon} /> : null}
      </div>
      {error || helper ? (
        <div
          className={classNames(
            'text-sm mt-xxs',
            error ? 'text-alert-danger' : 'text-text500',
          )}>
          {error || helper}
        </div>
      ) : null}
    </div>
  )
}

export default forwardRef(TextField)
